export const en = {
  login: {
    title: "Sign in to OrderHub",
    emailLabel: "Email address",
    emailPlaceholder: "you@example.com",
    passwordLabel: "Password",
    signIn: "Sign in",
    signingIn: "Signing in...",
    invalidCredentials: "Invalid username or password",
    forgotPassword: "Forgot password?",
    noAccount: "Don't have an account?",
    signUp: "Sign up",
  },
  signup: {
    fullNameLabel: "Full name",
    fullNamePlaceholder: "John Doe",
    nameRequired: "Name is required",
    emailLabel: "Email address",
    emailPlaceholder: "you@example.com",
    emailInvalid: "Please enter a valid email address",
    passwordLabel: "Password",
    passwordInvalid: "Password does not meet all requirements",
    confirmPasswordLabel: "Confirm password",
    passwordsDoNotMatch: "Passwords do not match",
    createAccount: "Create account",
    creatingAccount: "Creating account...",
    userExists: "This email is already associated with an account.",
    loginInstead: "Log in instead?",
    rateLimited: "Too many signup attempts. Please try again later.",
    signupFailed: "Signup failed",
  },
  forgotPassword: {
    title: "Forgot password?",
    subtitle: "Enter your email address and we'll send you a link to reset your password.",
    emailLabel: "Email address",
    emailPlaceholder: "you@example.com",
    emailRequired: "Please enter your email address.",
    sendResetLink: "Send Reset Link",
    sending: "Sending...",
    backToLogin: "Back to login",
    rememberPassword: "Remember your password?",
    checkInboxTitle: "Check your inbox",
    checkInboxBody: "If an account is registered for that email address, a password-reset link is on its way. Please check your inbox and spam folder.",
    linkExpiry: "The link will expire in 1 hour.",
    rateLimited: "Too many requests. Please try again later.",
    accountInactive:
      "Your account is inactive. Please contact your administrator.",
  },
  resetPassword: {
    title: "Set new password",
    subtitle: "Choose a strong password for your account.",
    newPasswordLabel: "New password",
    confirmPasswordLabel: "Confirm new password",
    submitButton: "Reset password",
    submitting: "Resetting password...",
    successTitle: "Password reset successful",
    successBody: "Your password has been updated. All other active sessions have been signed out for your security.",
    signInWithNewPassword: "Sign in with new password",
    invalidLinkTitle: "Invalid or expired link",
    invalidLinkBody: "This password reset link is invalid or has expired. Please request a new one.",
    requestNewLink: "Request new link",
    failed: "Failed to reset password. The link may have expired.",
    passwordInvalid: "Password does not meet all requirements",
    passwordsDoNotMatch: "Passwords do not match",
  },
  invitation: {
    title: "You're invited!",
    subtitle: "Join {{orgName}} on OrderHub",
    teamAssigned: "Team assigned",
    emailLabel: "Email",
    fullNameLabel: "Full Name",
    fullNamePlaceholder: "Enter your full name",
    passwordLabel: "Password",
    passwordPlaceholder: "Min. 8 characters",
    confirmPasswordLabel: "Confirm Password",
    confirmPasswordPlaceholder: "Confirm your password",
    submitButton: "Create Account & Join",
    submitting: "Creating account...",
    nameRequired: "Name is required",
    passwordTooShort: "Password must be at least 8 characters",
    passwordsDoNotMatch: "Passwords do not match",
    createFailed: "Failed to create account",
    somethingWrong: "Something went wrong. Please try again.",
    errorTitle: "Invitation Error",
    noInvitationId: "No invitation ID provided.",
    invalidInvitation: "This invitation is invalid, expired, or has already been used.",
    goToSignIn: "Go to Sign In",
  },
  oauth: {
    googleSignup: "Sign up with Google",
    googleLogin: "Continue with Google",
    googleFailed: "Google sign-in failed. Please try again.",
  },
  verification: {
    bannerText: "Please verify your email address to connect Shopify stores and create trigger rules.",
    resendButton: "Resend verification email",
    sending: "Sending...",
    rateLimited: "Please wait before retrying",
    sent: "Verification email sent!",
    failed: "Failed to resend verification email. Please try again.",
  },
  passwordRequirements: {
    title: "Password requirements",
    length: "At least 8 characters",
    upper: "One uppercase letter",
    lower: "One lowercase letter",
    number: "One number",
  },
  brandPanel: {
    headingLine1: "Manage your",
    headingLine2: "Shopify orders",
    headingLine3: "efficiently",
    description: "Streamline your order management with smart triggers, real-time tracking, and seamless team collaboration.",
    features: {
      trackingTitle: "Real-time order tracking",
      trackingDesc: "Monitor every order as it happens",
      triggersTitle: "Smart trigger rules",
      triggersDesc: "Automate actions based on order events",
      collaborationTitle: "Team collaboration",
      collaborationDesc: "Assign, claim, and resolve together",
    },
  },
  authPage: {
    welcomeBack: "Welcome back",
    welcomeBackSubtitle: "Sign in to your OrderHub account",
    createAccount: "Create your account",
    createAccountSubtitle: "Get started with OrderHub for free",
    loginTab: "Log in",
    signupTab: "Sign up",
    passwordResetSuccess: "Your password has been reset successfully. Please sign in with your new password.",
  },
  sessionExpired: {
    title: "Session Expired",
    description: "Your session has timed out due to {{minutes}} minutes of inactivity.",
    loggingOut: "Logging out...",
    logOut: "Log Out",
  },
  accountLocked: {
    title: "Account Locked",
    description: "Your workspace has a billing issue. Please contact your administrator to restore access.",
    contactAdmin: "Contact your administrator",
    signOut: "Sign out",
  },
  shopifyWelcome: {
    title: "Welcome to OrderHub",
    subtitle: "Create your account to get started",
    signupTab: "Sign up",
    loginTab: "Log in",
    quickSetupBadge: "Quick Setup — 2 minutes",
    panelHeadingLine1: "You're only",
    panelHeadingHighlight: "3 easy steps",
    panelHeadingLine3: "away",
    panelDescription:
      "Join thousands of Shopify merchants who save hours every week with smarter order management.",
    stepIndicatorLabel: "Step 1 of 3",
    stepIndicatorContext: "Create account",
    steps: {
      signupTitle: "Create your account",
      signupDesc: "Sign up with Google or email in seconds",
      connectTitle: "Connect your Shopify store",
      connectDesc: "One-click secure link to your store",
      teamTitle: "Invite your team",
      teamDesc: "Add teammates to collaborate on orders",
    },
    benefits: {
      freeTrial: "Free 14-day trial",
      noCreditCard: "No credit card required",
      cancelAnytime: "Cancel anytime",
    },
    poweredBy: "Powered by",
  },
} as const;
